/* Copyright (c) 2026 Xperi. All Rights Reserved. */

// TODO: We will remove this once signout feature is implemented,
// but for now this allows us to easily trigger the signout flow by entering "571247" on the remote.

import { InputAction } from "../../enums/InputAction";
import { Log } from "../../logging/Log";
import type { TInputEvent, InputInterceptor } from "../types/TInputEvent";

const TAG: string = "SecretCodeInterceptor";

/**
 * Time window (ms) within which the full code sequence must be entered.
 * If the window elapses between consecutive digits, the buffer resets.
 */
const CODE_TIMEOUT_MS: number = 5000;

/**
 * Maps NUM_* InputActions to their digit character.
 */
const ACTION_TO_DIGIT: ReadonlyMap<InputAction, string> = new Map([
  [InputAction.NUM_0, "0"],
  [InputAction.NUM_1, "1"],
  [InputAction.NUM_2, "2"],
  [InputAction.NUM_3, "3"],
  [InputAction.NUM_4, "4"],
  [InputAction.NUM_5, "5"],
  [InputAction.NUM_6, "6"],
  [InputAction.NUM_7, "7"],
  [InputAction.NUM_8, "8"],
  [InputAction.NUM_9, "9"]
]);

/**
 * Creates an interceptor that watches for a secret digit sequence.
 *
 * When the user types the exact sequence (e.g. "571247") using number keys
 * within the timeout window, the provided `onMatch` callback fires.
 * The interceptor only reacts to `keydown` events to avoid double-counting.
 *
 * The matched event is consumed (stopped) to prevent it from reaching
 * screen controllers.
 *
 * @param code     - The digit sequence to match (e.g. "571247").
 * @param onMatch  - Callback invoked when the full sequence is entered.
 */
export function createSecretCodeInterceptor(
  code: string,
  onMatch: () => void
): InputInterceptor {
  let buffer: string = "";
  let lastTimestamp: number = 0;

  return (event: TInputEvent): boolean | void => {
    if (event.eventType !== "keydown") {
      return;
    }

    const digit = ACTION_TO_DIGIT.get(event.action);
    if (digit === undefined) {
      return;
    }

    const now = event.timestamp;

    // Reset buffer if too much time has elapsed since the last digit.
    if (buffer.length > 0 && now - lastTimestamp > CODE_TIMEOUT_MS) {
      buffer = "";
    }

    lastTimestamp = now;
    buffer += digit;

    // If the buffer no longer matches the start of the code, trim from left
    // until it does (sliding window) or clear entirely.
    while (buffer.length > 0 && !code.startsWith(buffer)) {
      buffer = buffer.substring(1);
    }

    if (buffer === code) {
      Log.i(TAG, "createSecretCodeInterceptor(): Secret code matched");
      buffer = "";
      onMatch();
      return true;
    }
  };
}
